// Terms / Legal anchor navigation: scroll-spy for the section menus plus the
// mobile drawer that exposes the same links.
const TermsLegalNav = () => {
  const root = document.querySelector<HTMLElement>(".terms-legal")
  if (!root) return

  const links = Array.from(root.querySelectorAll<HTMLAnchorElement>("a.terms-legal-link"))
  const currentLabel = root.querySelector<HTMLElement>(".terms-legal-current")

  const targets = links
    .filter((link, index) => links.findIndex(other => other.hash === link.hash) === index)
    .filter(link => link.hash.length > 1)
    .map(link => ({
      hash: link.hash,
      label: link.textContent?.trim() ?? "",
      element: document.getElementById(decodeURIComponent(link.hash.slice(1))),
    }))
    .filter((target): target is typeof target & { element: HTMLElement } => target.element !== null)

  const setActive = (hash: string, label: string) => {
    links.forEach(link => link.classList.toggle("active", link.hash === hash))
    if (currentLabel) currentLabel.textContent = label
  }

  // Keep the offset larger than the sections' scroll-margin-top so the section
  // an anchor jump lands on is the one that lights up.
  const SPY_OFFSET = 120
  let spyScheduled = false

  const spy = () => {
    spyScheduled = false
    let current = targets[0]
    targets.forEach(target => {
      if (target.element.getBoundingClientRect().top <= SPY_OFFSET) current = target
    })
    if (current) setActive(current.hash, current.label)
  }

  window.addEventListener(
    "scroll",
    () => {
      if (spyScheduled) return
      spyScheduled = true
      window.requestAnimationFrame(spy)
    },
    { passive: true },
  )
  spy()

  const drawer = document.getElementById("terms-legal-drawer")
  const toggleButton = root.querySelector<HTMLButtonElement>(".terms-legal-menu-toggle")
  const closeButton = root.querySelector<HTMLButtonElement>(".terms-legal-drawer-close")
  if (!drawer || !toggleButton || !closeButton) return

  // Focus returns to the toggle only when the drawer is dismissed — after a link,
  // the fragment navigation owns focus and pulling it back would fight the jump.
  const setDrawerOpen = (open: boolean, { restoreFocus = true } = {}) => {
    drawer.hidden = !open
    toggleButton.setAttribute("aria-expanded", String(open))
    document.body.style.overflow = open ? "hidden" : ""
    if (open) closeButton.focus()
    else if (restoreFocus) toggleButton.focus()
  }

  toggleButton.addEventListener("click", () => setDrawerOpen(drawer.hidden))
  closeButton.addEventListener("click", () => setDrawerOpen(false))
  drawer.addEventListener("click", event => {
    if (event.target === drawer) setDrawerOpen(false)
  })
  document.addEventListener("keydown", event => {
    if (event.key === "Escape" && !drawer.hidden) setDrawerOpen(false)
  })
  drawer.querySelectorAll("a").forEach(link => {
    link.addEventListener("click", () => setDrawerOpen(false, { restoreFocus: false }))
  })

  // The dialog is aria-modal, so Tab must not reach the page behind it.
  drawer.addEventListener("keydown", event => {
    if (event.key !== "Tab") return
    const focusables = drawer.querySelectorAll<HTMLElement>("button, a[href]")
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  })

  // Matches the SCSS small-desktop-and-up breakpoint, where the drawer and its
  // toggle are hidden — close it so the scroll lock doesn't outlive them.
  const desktop = window.matchMedia("(min-width: 1025px)")
  desktop.addEventListener("change", event => {
    if (event.matches && !drawer.hidden) setDrawerOpen(false, { restoreFocus: false })
  })
}

export default TermsLegalNav
